// ════════════════════════════════════════════════════════════════════
// Stage 6PL-1e — the customer-swap diff (decision D3).
//
// "On a customer swap, ask — but ONLY when it would actually change a rate."
//
// PURE, so the rule can be tested without a browser. Three refusals are the
// whole substance of it:
//
//   1. A MANUAL line is NEVER offered. A hand-typed rate is somebody's
//      decision, and silently replacing it is the exact failure the document
//      stamp (migration 0182) was added to prevent. It is excluded before any
//      comparison happens, not merely deselected in the dialog.
//
//   2. AN EMPTY DIFF IS SILENCE. A prompt that fires when nothing would change
//      trains the operator to dismiss it without reading — and then it is worth
//      nothing on the day it matters. No customer, no items, same rates: no
//      dialog.
//
//   3. A MISS IS THE MASTER, NOT A ZERO. A ref the new customer's lists do not
//      carry falls back to the item master's own rate, exactly as the server
//      would. Reading a miss as 0 would propose zeroing every unlisted line.
// ════════════════════════════════════════════════════════════════════

const SQFT_PER_SQM = 10.764;

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
const round2 = (v) => Math.round((num(v) + Number.EPSILON) * 100) / 100;
// A RATE is an intermediate, not a ledger entry — NUMERIC(14,4) under CLAUDE.md
// rule 4, which is what sales_rate / alternate_rate actually are. This MUST
// match the backend's convertAreaRate: the proposal below is written straight
// onto the line, so a 2-dp rate here and a 4-dp rate on the server's own save
// path would make the editor and the saved document disagree about the same
// agreed rate. round2 stays for the "is this change worth offering?" threshold
// and nothing else.
const round4 = (v) => Math.round((num(v) + Number.EPSILON) * 1e4) / 1e4;

// The proforma enum is SQF/SQM; the masters also spell them SQFT/SQMT. Both
// sides normalise before comparing, or an identical pair reads as a mismatch
// and a needed conversion is skipped.
export function normUom(u) {
  const s = String(u || '').trim().toUpperCase();
  if (s === 'SQFT') return 'SQF';
  if (s === 'SQMT' || s === 'M2') return 'SQM';
  return s;
}

/**
 * A rate per `fromUom` expressed per `toUom`. Only the two AREA units convert;
 * anything else is returned unchanged, because a per-piece rate has no area
 * equivalence and inventing one would be worse than leaving it alone.
 * Mirrors the backend's convertAreaRate.
 */
export function convertAreaRate(rate, fromUom, toUom) {
  const r = num(rate);
  const f = normUom(fromUom);
  const t = normUom(toUom);
  if (!f || !t || f === t) return r;
  if (!['SQF', 'SQM'].includes(f) || !['SQF', 'SQM'].includes(t)) return r;
  return round4(f === 'SQM' ? r / SQFT_PER_SQM : r * SQFT_PER_SQM);
}

/**
 * The OTHER area UOM — SQF for a SQM document and vice versa; null for anything
 * that is not an area unit. PCS / RFT / RMT documents have no second price
 * column at all, and convertAreaRate passes a non-area unit through unchanged,
 * so without this guard a per-piece rate lands in an area cell.
 */
export function siblingAreaUom(uom) {
  const u = normUom(uom);
  if (u === 'SQM') return 'SQF';
  if (u === 'SQF') return 'SQM';
  return null;
}

/** Matches the backend resolver's refKey for an ITEM. */
export function itemRefKey(itemId) {
  return `ITEM:${String(itemId)}:`;
}

/**
 * What would change if this document were priced for a different customer.
 *
 * @param {Array}  lines        the editor's rows
 * @param {object} rates        resolvePricesSafe(...).rates — refKey -> hit
 * @param {string} docPriceUom  the document's selling UOM
 * @returns {Array} one entry per line whose rate WOULD move:
 *          { refUuid, lineNo, itemDesc, from, to, listCode, source, priceListId }
 */
export function buildRepriceDiff({ lines, rates, docPriceUom }) {
  const rows = Array.isArray(lines) ? lines : [];
  const map = (rates && typeof rates === 'object') ? rates : {};
  const out = [];

  for (const l of rows) {
    if (!l || !l.itemId) continue;                       // free-typed line: nothing to resolve
    // Refusal 1 — a hand-typed rate is never proposed for replacement.
    if (String(l.priceSource || '').toUpperCase() === 'MANUAL') continue;

    const current = num(l.salesRate);
    const hit = map[itemRefKey(l.itemId)];
    // Refusal 3 — a miss means the MASTER rate, which is what the item snapshot
    // carries. With no snapshot there is nothing trustworthy to compare against,
    // so the line is left alone rather than guessed at.
    const master = (l.itemSnapshot && l.itemSnapshot.salesRate != null)
      ? num(l.itemSnapshot.salesRate) : null;
    let next;
    if (hit) {
      next = convertAreaRate(hit.rate, hit.priceUomCode, l.priceUomCode || docPriceUom);
    } else if (master != null) {
      next = master;
    } else {
      continue;
    }

    if (Math.abs(round2(next) - round2(current)) < 0.005) continue;   // Refusal 2

    out.push({
      refUuid: l.refUuid,
      lineNo: l.lineNo,
      itemDesc: l.itemDesc || '',
      from: round4(current),
      to: round4(next),
      listCode: hit ? (hit.priceListCode || null) : null,
      source: hit ? (hit.source || 'CUSTOMER_LIST') : 'MASTER',
      priceListId: hit ? (hit.priceListId || null) : null
    });
  }
  return out;
}

/**
 * Apply an accepted diff. Returns a NEW array; every untouched line is the SAME
 * object, so React's reconciliation and the grid's memoised rows see exactly
 * which rows moved.
 *
 * The stamp is rewritten alongside the rate — a re-priced line must stop
 * claiming the old card, or the explain panel would go on naming an agreement
 * that no longer set its number.
 */
export function applyReprice(lines, diff) {
  const byRef = new Map((Array.isArray(diff) ? diff : []).map((d) => [d.refUuid, d]));
  if (!byRef.size) return lines;
  return (Array.isArray(lines) ? lines : []).map((l) => {
    const d = l && byRef.get(l.refUuid);
    if (!d) return l;
    const sib = siblingAreaUom(l.priceUomCode);
    return {
      ...l,
      salesRate: d.to,
      // The sibling PRICE column follows, exactly as syncRatePair would on a
      // hand edit — otherwise the two cells disagree after a re-price. A
      // non-area document has no sibling column, so its value is left alone.
      alternateRate: sib ? convertAreaRate(d.to, l.priceUomCode, sib) : l.alternateRate,
      priceListId: d.priceListId,
      priceSource: d.source
    };
  });
}
